import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Tooltip, Typography, IconButton, Icon, RadioGroup, FormControlLabel, Radio, Button, useTheme, useMediaQuery, Snackbar, Alert } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import OccupantsIcon from '../../../icons/AdditionalOccupants.svg';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import EditIcon from '../../../icons/editIcon.svg';
import DeleteIcon from '../../../icons/deleteIcon.svg';
import { UpsertApplicantMinorV2Dto, ApplicantMinorV2Dto, ApplicantV2Dto, ApplicantV2DtoStatusEnum } from '@inosio/payments-sdk';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import DeleteModal from './DeleteModal';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import AddCoApplicantsModal from './AddCoApplicantsModal';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { useCallback, useEffect, useState } from 'react';
import {
  clearCreateMinorRecord,
  createMinorRecord,
  deleteMinorRecord,
  getApplicantV2,
  getApplicationV2,
  getMinorRecords,
  removeApplicantV2,
  resetInviteApplicantState,
  selectApplicantsState,
  updateApplicantV2,
} from '../sagas/applicants/applicantsSlice';
import { useHistory, useRouteMatch } from 'react-router';
import { CoapplicantValidator, MinorOccupantValidator } from '../validators/ApplicantValidators';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatStringToSentenceCase } from '../../../shared/util/formaters';

interface CosignersCooccupantsPropsType {
  CoapplicantsValidator: CoapplicantValidator;
  MinorOccupantsValidator: MinorOccupantValidator;
}

export default function CosignersCooccupants(props: CosignersCooccupantsPropsType) {
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const classes = applicationStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  let { url } = useRouteMatch();
  const theme = useTheme();
  const isOnMobile = useMediaQuery(theme.breakpoints.down('sm')); //using material ui media query for consistency
  const [snackBarOpen, setSnackBarOpen] = useState(false);

  //selectors
  const { ipaUser } = useSelector(selectAuthState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);
  const {
    Applicant,
    isFetchingApplicantSuccess,
    isFetchingApplicantFailure,
    Application,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isCreatingMinorRecordSuccess,
    isFetchingCustomQuestionAnswersSuccess,
    isDeletingMinorRecordSuccess,
    isDeletingMinorRecordFailed,
    createdMinorRecord,
    MinorRecords,
    isFetchingMinorRecordsFailed,
    isFetchingMinorRecordsSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isRemovingApplicantSuccess,
    isRemovingApplicantFailed,
  } = useSelector(selectApplicantsState);

  //local states
  const [localError, setLocalError] = useState<string | null>(null);
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [showMinorSection, setShowMinorSection] = useState(false);
  const [hasNoMinor, setHasNoMinor] = useState<boolean | null>(null); //local state for keeping track of what to show in the radio
  const [isCurrenetUserThePrimaryApplicant, setIsCurrenetUserThePrimaryApplicant] = useState(false); //to allow whether or not to add and remove co-applicants
  const [openAddCoApplicantsModal, setOpenAddCoApplicantsModal] = useState(false);
  const [selectedMinorUuid, setSelectedMinorUuid] = useState<string>('');
  const [isDeleteMinorModalOpen, setIsDeleteMinorModalOpen] = useState(false);
  const [isDeleteCoApplicantModalOpen, setIsDeleteCoApplicantModalOpen] = useState(false);
  const [selectedCoApplicantUuid, setSelectedCoApplicantUuid] = useState('');
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState<string | null>(null);
  const [doesThisAppHaveSomeMinors, setDoesThisAppHaveSomeMinors] = useState(false); //boolean flag to help control the grammar of the text to the user.
  const [otherApplicantsMinors, setOtherApplicantsMinors] = useState<ApplicantMinorV2Dto[]>([]); //keep the current Applicant's minors separate from "everybody" elses so we can show the ghost text.
  const [otherApplicants, setOtherApplicants] = useState<ApplicantV2Dto[]>([]);
  //#region v2 useEffects

  //when they click create new minor, api request succeeds, listen to this
  useEffect(() => {
    if (isCreatingMinorRecordSuccess && createdMinorRecord?.Uuid) {
      dispatch(clearCreateMinorRecord());
      history.push(`${url}/${createdMinorRecord?.Uuid}`);
    }
  }, [isCreatingMinorRecordSuccess, createdMinorRecord]);

  //fetch minors and coapplicants on load. Coapplicants is just a factor of fetching the application, which the wizard has already done.
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch(getMinorRecords(ipaUser?.currentProfile.uuid));
      dispatch(getApplicantV2());
    }
  }, [ipaUser]);

  useEffect(() => {
    if (isDeletingMinorRecordSuccess) {
      setSpinnerStatus('stop');
      dispatch(getMinorRecords(ipaUser?.currentProfile.uuid));
      dispatch(getApplicationV2(Application.Uuid));
    }

    if (isDeletingMinorRecordFailed) {
      setSpinnerStatus('stop');
    }
  }, [isDeletingMinorRecordSuccess, isDeletingMinorRecordFailed]);

  useEffect(() => {
    if (isRemovingApplicantSuccess) {
      setSpinnerStatus('stop');
      dispatch(getApplicationV2(Application.Uuid));
    }

    if (isRemovingApplicantFailed) {
      setSpinnerStatus('stop');
    }
  }, [isRemovingApplicantSuccess, isRemovingApplicantFailed]);

  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess) setShowMinorSection(!!propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsAllowAdditionalOccupants);
  }, [propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess]);

  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingPropertyGroupDetailsSuccess && isFetchingMinorRecordsSuccess && isFetchingApplicationSuccess && isFetchingCustomQuestionAnswersSuccess && isFetchingApplicantSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
      setIsCurrenetUserThePrimaryApplicant(Application.PrimaryApplicant.Uuid === ipaUser?.currentProfile.uuid);
    } else if (isFetchingPropertyGroupDetailsFailed || isFetchingMinorRecordsFailed || isFetchingApplicationFailure || isFetchingCustomQuestionAnswersFailure || isFetchingApplicantFailure) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [
    isFetchingPropertyGroupDetailsSuccess,
    isFetchingPropertyGroupDetailsFailed,
    isFetchingApplicationSuccess,
    isFetchingApplicationFailure,
    isFetchingCustomQuestionAnswersSuccess,
    isFetchingCustomQuestionAnswersFailure,
    isFetchingMinorRecordsSuccess,
    isFetchingMinorRecordsFailed,
    isFetchingApplicantSuccess,
    isFetchingApplicantFailure,
  ]);

  //if the minor count changes (ie, they deleted one), and there are none left AND there is a selectedMinorUuid (indicating he just clicked delete) then we need to unwind the fact that he selected "i have minors" null
  useEffect(() => {
    if (isFetchingMinorRecordsSuccess && MinorRecords.length < 1 && selectedMinorUuid) {
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: Applicant.Uuid,
          updateDto: { HasNoMinors: null },
        },
      });
    }
  }, [isFetchingMinorRecordsSuccess, MinorRecords, selectedMinorUuid]);

  useEffect(() => {
    if (isFetchingApplicantSuccess) {
      setHasNoMinor(Applicant.HasNoMinors ?? null);
    }
  }, [Applicant, isFetchingApplicantSuccess]);

  //build up the not-mine-minors list
  useEffect(() => {
    //* This approach can be used for any list based entity that belongs to any applicant (vehicles, pets, etc)
    if (!isFetchingMinorRecordsSuccess || !isFetchingApplicationSuccess) return;
    //MinorRecords represents current users
    //Application.[primary]/[coapps] minors represent everybody elses
    const allMinorsSuperSet = [...(Application.PrimaryApplicant.Minors ?? []), ...Application.CoApplicants.flatMap((ca) => ca.Minors ?? [])];
    setDoesThisAppHaveSomeMinors(!!allMinorsSuperSet.length);
    const minorsThatAreNotCurrentUser = allMinorsSuperSet.filter((m) => !MinorRecords.find((mr) => mr.Uuid === m.Uuid));
    setOtherApplicantsMinors(minorsThatAreNotCurrentUser);

    //get all applicants other than the current user
    const allOtherApplicants = [Application.PrimaryApplicant, ...Application.CoApplicants].filter((a) => a.Uuid !== ipaUser?.currentProfile.uuid);
    setOtherApplicants(allOtherApplicants);
  }, [isFetchingMinorRecordsSuccess, MinorRecords, isFetchingApplicationSuccess, Application]);
  //#endregion v2 useEffects

  const handleUpdateHasMinor = (e: any) => {
    const selectedHasMinor = e.target.value === 'true';

    if (selectedHasMinor) {
      const newMinorDto: UpsertApplicantMinorV2Dto = {};
      dispatch(createMinorRecord({ applicantUuid: ipaUser?.currentProfile.uuid, minorDto: newMinorDto }));
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: Applicant.Uuid,
          updateDto: { HasNoMinors: false },
        },
      });
      setHasNoMinor(false);
    } else {
      dispatch({
        type: updateApplicantV2.type,
        payload: {
          applicantUuid: Applicant.Uuid,
          updateDto: { HasNoMinors: true },
        },
      });
      setHasNoMinor(true);
    }
  };

  const handleCloseAddCoApplicantModal = () => {
    setOpenAddCoApplicantsModal(false);
    dispatch(resetInviteApplicantState());
  };

  const handleConfirmDeleteMinorModal = () => {
    setIsDeleteMinorModalOpen(false);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: deleteMinorRecord.type,
      payload: { applicantUuid: ipaUser?.currentProfile.uuid, minorUuid: selectedMinorUuid },
    });
  };

  const handleConfirmDeleteCoApplicantModal = () => {
    setIsDeleteCoApplicantModalOpen(false);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: removeApplicantV2.type,
      payload: {
        applicantUuid: selectedCoApplicantUuid,
        applicationUuid: Application.Uuid,
      },
    });
  };

  const handleCloseSpinnerModal = useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, [dispatch]);

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', minHeight: 'inherit', p: 4 }}>
          {localError && (
            <Box sx={{ width: '100%', justifyContent: 'center', textAlign: 'center' }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Box>
          )}
          <Typography sx={{}} className={classes.labelText}>
            {`Additional Occupants`}
          </Typography>

          <Typography sx={{ mt: 2 }} className={classes.bodyText}>
            All adult occupants and co-signers must complete their own application. Add them below to link them to your application. They will receive a link to complete their own data.
          </Typography>

          {showMinorSection && (
            <Typography sx={{ mt: 1, mb: 2 }} className={classes.bodyText}>
              Non-adult Occupants also need to be added to your application to verify occupancy limits are not exceeded.
            </Typography>
          )}

          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid item xs={12} sm={showMinorSection ? 6 : 12} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box className={classes.cardContainer}>
                <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                  <Box sx={{}}>
                    <Typography className={classes.cardLabelText}>
                      Co-applicants & Co-signers
                      <Tooltip
                        title="Non-adult people who live with you. You can also include adults who won't be included on the lease. If you need another occupant to qualify for the property, they will need to submit their own application."
                        placement="right"
                      >
                        <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                      </Tooltip>
                    </Typography>
                  </Box>

                  {isCurrenetUserThePrimaryApplicant && (
                    <Button
                      type="button"
                      className={classes.wizardAddNewBtn}
                      onClick={() => {
                        setOpenAddCoApplicantsModal(true);
                      }}
                    >
                      <Typography className={classes.wizardAddNewBtnText} sx={{ margin: '0 10px' }}>
                        {`Add New`}
                      </Typography>
                    </Button>
                  )}
                </Box>
                <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {Application.CoApplicants?.length ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {otherApplicants?.map((occupant) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={occupant.Uuid}>
                          <Box sx={{ width: '100%', pr: 6 }}>
                            {occupant?.Status === ApplicantV2DtoStatusEnum.Completed ? (
                              <img
                                style={{
                                  width: '12px',
                                  height: '9px',
                                  marginRight: '10px',
                                }}
                                src={CheckMarkIcon}
                                alt="Completed"
                              />
                            ) : (
                              <img
                                style={{
                                  width: '12px',
                                  height: '16px',
                                  marginRight: '10px',
                                }}
                                src={IncompleteIcon}
                                alt="Incomplete"
                              />
                            )}
                            {occupant.FirstName} {occupant.LastName}
                            <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                              {occupant.ApplicantType}
                            </Typography>
                          </Box>
                          <Box sx={{ position: 'absolute', left: '50%', height: '100%', display: 'flex', alignItems: 'center' }}>
                            <Typography className={classes.grayLabelText}>
                              {Application?.PrimaryApplicant.Uuid === occupant.Uuid ? `Primary` : formatStringToSentenceCase(occupant.Status) || ''}
                            </Typography>
                          </Box>
                          {isCurrenetUserThePrimaryApplicant && (
                            <Box sx={{ position: 'absolute', right: 16, height: '100%', display: 'flex', alignItems: 'center' }}>
                              {occupant.InvitationLink && (
                                <IconButton
                                  edge="start"
                                  color="inherit"
                                  aria-label="Copy invitation link"
                                  onClick={() => {
                                    navigator.clipboard.writeText(occupant.InvitationLink!);
                                    setSnackBarOpen(true);
                                  }}
                                >
                                  <ContentCopyIcon sx={{ width: '14px', height: '14px', fill: '#14142B' }} />
                                </IconButton>
                              )}
                              <IconButton
                                onClick={() => {
                                  setSelectedCoApplicantUuid(occupant.Uuid!);
                                  setIsDeleteCoApplicantModalOpen(true);
                                }}
                                sx={{ p: 0.25, mr: '-2px' }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '14px',
                                      height: '14px',
                                    }}
                                    src={DeleteIcon}
                                    alt="Delete"
                                  />
                                </Icon>
                              </IconButton>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ height: 170, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <img
                        style={{
                          width: '94px',
                          height: '94px',
                        }}
                        src={OccupantsIcon}
                        alt="add co-applicant"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>

            {showMinorSection && (
              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                <Box className={classes.cardContainer}>
                  <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                    <Grid container sx={{ width: '100%' }}>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'start', alignItems: 'center' }}>
                        <Typography className={classes.cardLabelText}>
                          Non-Adult Occupants
                          <Tooltip
                            title="Non-adult people who live with you. You can also include adults who will not be included on the lease. If you need another occupant to qualify for the property, they will need to submit their own application."
                            placement="right"
                          >
                            <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                          </Tooltip>
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: isOnMobile ? 'center' : 'end', alignItems: 'center' }}>
                        <Button
                          type="button"
                          className={classes.wizardAddNewBtn}
                          onClick={() => {
                            dispatch(createMinorRecord({ applicantUuid: ipaUser?.currentProfile.uuid, minorDto: {} }));
                          }}
                        >
                          <Typography className={classes.wizardAddNewBtnText} sx={{ margin: '0 10px' }}>
                            {`Add New`}
                          </Typography>
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                  <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {MinorRecords?.length ? (
                        MinorRecords.map((minorRecord) => (
                          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={minorRecord.Uuid}>
                            <Box sx={{ width: '100%', pr: 6 }}>
                              {props.MinorOccupantsValidator.IsEntireObjectComplete(minorRecord) ? (
                                <img
                                  style={{
                                    width: '12px',
                                    height: '9px',
                                    marginRight: '10px',
                                  }}
                                  src={CheckMarkIcon}
                                  alt="Completed"
                                />
                              ) : (
                                <img
                                  style={{
                                    width: '12px',
                                    height: '16px',
                                    marginRight: '10px',
                                  }}
                                  src={IncompleteIcon}
                                  alt="Incomplete"
                                />
                              )}
                              {minorRecord.FirstName || '[Missing First Name]'} {minorRecord.LastName || '[Missing Last Name]'}
                              <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                {Applicant.FirstName} {Applicant.LastName}
                              </Typography>
                            </Box>
                            <Box sx={{ position: 'absolute', right: 16, height: '100%', display: 'flex', alignItems: 'center' }}>
                              <IconButton
                                onClick={() => {
                                  setSelectedMinorUuid(minorRecord.Uuid!);
                                  setIsDeleteMinorModalOpen(true);
                                }}
                                sx={{ p: 0.25, mr: '-2px' }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '14px',
                                      height: '14px',
                                    }}
                                    src={DeleteIcon}
                                    alt="Delete"
                                  />
                                </Icon>
                              </IconButton>
                              <IconButton
                                onClick={() => {
                                  history.push(`${url}/${minorRecord.Uuid}${hasValidationQueryParam ? '?validation=true' : ''}`);
                                }}
                                sx={{ p: 0.25, mr: '-2px' }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '14px',
                                      height: '14px',
                                    }}
                                    src={EditIcon}
                                    alt="Edit"
                                  />
                                </Icon>
                              </IconButton>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', p: 2, borderBottom: '1.5px solid #F1F0F5' }}>
                          <Typography sx={{ mr: 3 }} className={classes.bodyText}>
                            Are there any {doesThisAppHaveSomeMinors ? 'additional' : ''} non-adult occupants?
                          </Typography>
                          <RadioGroup value={hasNoMinor === true ? false : null} row onChange={handleUpdateHasMinor}>
                            <FormControlLabel value={false} control={<Radio size="small" />} sx={hasNoMinor == null && hasValidationQueryParam ? { color: 'red' } : {}} label="No" />
                            <FormControlLabel value={true} control={<Radio size="small" />} sx={hasNoMinor == null && hasValidationQueryParam ? { color: 'red' } : {}} label="Yes" />
                          </RadioGroup>
                        </Box>
                      )}
                      {otherApplicantsMinors.map((oam) => {
                        const thisMinorsParent =
                          Application?.PrimaryApplicant.Uuid === oam.ParentOccupantUuid ? Application?.PrimaryApplicant : Application?.CoApplicants?.find((c) => c.Uuid === oam.ParentOccupantUuid);

                        let parentText = '';
                        if (thisMinorsParent) {
                          parentText = `${thisMinorsParent.FirstName} ${thisMinorsParent.LastName}`;
                        }

                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={oam.Uuid}>
                            <Box sx={{ width: '100%', pr: 6 }}>
                              <Typography sx={{ ml: 3 }}>
                                {oam.FirstName} {oam.LastName}
                              </Typography>
                              <Typography sx={{ ml: 3 }} className={classes.grayLabelText}>
                                {parentText}
                              </Typography>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      )}

      {openAddCoApplicantsModal && <AddCoApplicantsModal open={openAddCoApplicantsModal} applicationUuid={Application.Uuid} closeCallback={handleCloseAddCoApplicantModal} />}

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          fastSpinner={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      {isDeleteMinorModalOpen && <DeleteModal open={isDeleteMinorModalOpen} confirmCallback={() => handleConfirmDeleteMinorModal()} closeCallback={() => setIsDeleteMinorModalOpen(false)} />}
      {isDeleteCoApplicantModalOpen && (
        <DeleteModal open={isDeleteCoApplicantModalOpen} confirmCallback={handleConfirmDeleteCoApplicantModal} closeCallback={() => setIsDeleteCoApplicantModalOpen(false)} />
      )}
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        open={snackBarOpen}
        autoHideDuration={4000}
        onClose={() => {
          setSnackBarOpen(false);
        }}
      >
        <Alert
          onClose={() => {
            setSnackBarOpen(false);
          }}
          severity="success"
          sx={{ width: '100%' }}
        >
          Copied to Clipboard
        </Alert>
      </Snackbar>
    </Box>
  );
}
